import { memo, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  FlatList,
  Keyboard,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  View,
} from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import styled, { useTheme } from 'styled-components'
import { Button } from '@flix/common/elements/controls'
import { Paragraph } from '@flix/common/elements/typography'
import { useInvertScrollWeb } from '@flix/common/hooks/useInvertScroll/useInvertScrollWeb'
import { useMediaSelect } from '@flix/common/style/media'
import { useChatContext } from '../../ChatContext'
import messages from '../../messages'
import { UserIcon } from '../UserIcon'
import { ChatTextInput } from './ChatTextInput/ChatTextInput'

const KeyboardAvoidingContainer = styled(KeyboardAvoidingView)`
  flex: 1;
  background: ${(props) => props.theme.colors.grayLighter};
`

const PlaceholderWhileWatchingOnTV = styled(View)`
  padding: ${(props) => props.theme.spacing.md}px;
  padding-top: ${(props) => props.topInset || props.theme.spacing.md}px;
  background: ${(props) => props.theme.colors.black};
`

const PlaceHolderTextWhileWatchingOnTV = styled(Paragraph)`
  text-align: center;
`

const ChatHeader = styled(View)`
  background: ${(props) => props.theme.colors.background};
  flex-direction: row;
  padding: ${(props) => `${props.theme.spacing.sm}px`};
  align-items: center;
  justify-content: space-between;
`

const Row = styled(View)`
  flex-direction: row;
  align-items: center;
`

const ChatHeaderText = styled(Paragraph)`
  margin: 0;
`

const MessageRow = styled(View)`
  flex-direction: row;
  align-items: center;
  padding: ${(props) => props.theme.spacing.sm}px;
  padding-bottom: 0;
`

const MessageText = styled(Paragraph)`
  color: ${(props) => props.theme.colors.black};
  margin: 0;
  margin-left: ${(props) => props.theme.spacing.sm}px;
  flex: 1;
`

export const GroupWatchChatDock = memo(({ fullChatVisible = true, chatPosition = 'right' }) => {
  const { users, sendMessage, chatMessages, activeUsers, userIsWatchingOnTV, onBack } =
    useChatContext()

  const chatMessageListRef = useRef()
  const theme = useTheme()

  // We need to invert scroll behaviour here due to an issue using `inverted` on FlatList in react-native-web
  useInvertScrollWeb(chatMessageListRef)

  const { chatUserIconSize, topBarUserIconSize } = useMediaSelect({
    xs: { chatUserIconSize: 30, topBarUserIconSize: chatPosition === 'right' ? 20 : 30 },
    sm: { chatUserIconSize: 35, topBarUserIconSize: chatPosition === 'right' ? 25 : 30 },
    md: { topBarUserIconSize: chatPosition === 'right' ? 30 : 35 },
    lg: { topBarUserIconSize: 35 },
  })

  const renderMessageRow = useCallback(
    ({ item }) => <ChatMessage item={item} size={chatUserIconSize} user={users[item.memberId]} />,
    [chatUserIconSize, users],
  )

  const { top } = useSafeAreaInsets()

  return (
    fullChatVisible && (
      <KeyboardAvoidingContainer behavior="padding">
        <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
          <>
            {userIsWatchingOnTV && (
              <PlaceholderWhileWatchingOnTV topInset={top}>
                <PlaceHolderTextWhileWatchingOnTV style={{ fontWeight: 'bold' }}>
                  <FormattedMessage {...messages.watchingTVHeader} />
                </PlaceHolderTextWhileWatchingOnTV>
                <PlaceHolderTextWhileWatchingOnTV>
                  <FormattedMessage {...messages.watchingTVSubheader} />
                </PlaceHolderTextWhileWatchingOnTV>
                <Button
                  style={{ marginBottom: 0 }}
                  link
                  underline
                  intlMsg={messages.closeChat}
                  onPress={onBack}
                />
              </PlaceholderWhileWatchingOnTV>
            )}
            <ChatHeader>
              <Row>
                <ChatHeaderText>
                  <FormattedMessage {...messages.chatHeader} />
                </ChatHeaderText>
              </Row>
              <Row>
                {activeUsers
                  // UI does not work for >8 active users. Until this is a problem we can just clip
                  // the array.
                  .slice(0, 8)
                  .map(
                    (id) =>
                      users[id] && (
                        <UserIcon
                          key={id}
                          user={users[id]}
                          size={topBarUserIconSize}
                          style={{ alignSelf: 'auto', marginLeft: theme.spacing.xs }}
                        />
                      ),
                  )}
              </Row>
            </ChatHeader>
            <FlatList
              inverted
              // React Native Web has issues with rendering large inverted lists, which are fixed by disabling virtualization
              // As such we should not pass very large arrays into this FlatList
              disableVirtualization
              initialNumToRender={50}
              ref={chatMessageListRef}
              keyExtractor={(item) => item.id}
              data={chatMessages}
              renderItem={renderMessageRow}
              style={{ marginBottom: theme.spacing.sm }}
              contentContainerStyle={{ padding: theme.spacing.xs }}
            />
          </>
        </TouchableWithoutFeedback>
        <ChatTextInput sendMessage={sendMessage} />
      </KeyboardAvoidingContainer>
    )
  )
})

const ChatMessage = memo(({ item: { text }, size, user }) => (
  <MessageRow>
    <UserIcon size={size} user={user} />
    <MessageText>{text}</MessageText>
  </MessageRow>
))
